import { IdGenerator } from '@src/editor/services/IdGenerator';

import type { KaraokeData, KaraokeLine } from '@src/editor/types/karaoke';
import type {
  TranscriptElement,
  TranscriptElementLegacy,
} from '@src/types.ts/transcript';
import type { TranscriptToken } from '@src/editor/types';
import { KaraokeConfig } from '../models/karaoke/KaraokeConfig';

/**
 * Parses legacy transcript elements with karaoke break markers into KaraokeData format.
 *
 * This function:
 * 1. Takes legacy transcript elements with karaoke_break markers
 * 2. Segments them into clips based on karaoke_break markers
 * 3. Returns KaraokeData with clips and empty configs
 *
 * @param transcriptElements - Legacy transcript elements with karaoke metadata
 * @returns KaraokeData with clips segmented by legacy karaoke breaks
 */
export function parseKaraokeDataFromLegacyTranscriptElements(
  transcriptElements: TranscriptElementLegacy[],
  migratedConfig: KaraokeConfig,
): KaraokeData | null {
  // Filter out removed/cut elements
  const activeElements = transcriptElements.filter(
    el => el.state !== 'removed' && el.state !== 'cut',
  );

  // Segment elements into clips based on karaoke_break markers
  const clips = segmentElementsIntoClips(activeElements, migratedConfig);

  // Return KaraokeData format (configs will be populated later by the caller)
  return {
    clips,
    configs: new Map([[migratedConfig.id, migratedConfig]]),
    defaultConfigId: migratedConfig.id,
  };
}

/**
 * Segment transcript elements into clips based on karaoke_break markers
 *
 * @param elements - Array of legacy transcript elements
 * @returns Array of clip objects matching KaraokeData.clips format
 */
function segmentElementsIntoClips(
  elements: TranscriptElementLegacy[],
  migratedConfig: KaraokeConfig,
): KaraokeData['clips'] {
  const clips: KaraokeData['clips'] = [];
  let currentClipElements: TranscriptElementLegacy[] = [];

  for (let i = 0; i < elements.length; i++) {
    const element = elements[i];

    // Add element to current clip
    currentClipElements.push(element);

    // Check if we should end the current clip
    if (shouldEndSegment(element)) {
      // Create clip from current elements
      const clip = createClipFromElements(currentClipElements, migratedConfig);

      if (clip) {
        clips.push(clip);
      }

      // Reset for next clip
      currentClipElements = [];
    }
  }

  // Add remaining elements as final clip
  if (currentClipElements.length > 0) {
    const clip = createClipFromElements(currentClipElements, migratedConfig);

    if (clip) {
      clips.push(clip);
    }
  }

  return clips;
}

/**
 * Check if element is muted (should be hidden but still included in clip)
 * Muted elements are added to hiddenTranscriptTokenIds
 */
function isElementMuted(element: TranscriptElementLegacy): boolean {
  // Check if explicitly muted
  if (element.state === 'muted') {
    return true;
  }

  // Check elements muted by karaoke config
  if (element.muted_by_hideFillers || element.muted_by_hidePunct) {
    return true;
  }

  return false;
}

/**
 * Determine if we should end the current segment at this element
 * Based on karaoke break markers in legacy transcript
 */
function shouldEndSegment(element: TranscriptElementLegacy): boolean {
  // Check for karaoke break marker
  return element.karaoke_break === true;
}

/**
 * Create a clip object from element array
 * Includes all elements but marks muted elements in hiddenTranscriptTokenIds
 */
function createClipFromElements(
  elements: TranscriptElementLegacy[],
  migratedConfig: KaraokeConfig,
): KaraokeData['clips'][0] | null {
  if (elements.length === 0) {
    return null;
  }

  // Find text elements for timing validation
  const textElements = elements.filter(el => el.type === 'text');
  if (textElements.length === 0) {
    return null;
  }

  // Check if we have any visible (non-muted) text elements
  const visibleTextElements = textElements.filter(el => !isElementMuted(el));

  if (visibleTextElements.length === 0) {
    return null;
  }

  // Collect IDs of muted elements to add to hiddenTranscriptTokenIds
  const hiddenTokenIds = elements
    .filter(el => isElementMuted(el))
    .map(el => el.id!)
    .filter(id => id !== undefined);

  // Get element IDs for the clip
  const transcriptTokenIds = elements
    .map(el => el.id!)
    .filter(id => id !== undefined);

  // Ensure all elements have IDs
  if (transcriptTokenIds.length !== elements.length) {
    console.warn('Some legacy elements are missing IDs');
  }

  // Get start/end time offsets from karaoke break metadata
  const startSecondsOffset = elements.find(
    el => el.karaoke_break_start_ts_diff !== undefined,
  )?.karaoke_break_start_ts_diff;
  const endSecondsOffset = elements.find(
    el => el.karaoke_break_end_ts_diff !== undefined,
  )?.karaoke_break_end_ts_diff;

  return {
    id: IdGenerator.shortRandomId(),
    transcriptTokenIds,
    hiddenTranscriptTokenIds:
      hiddenTokenIds.length > 0 ? hiddenTokenIds : undefined,
    ignoredByFiltersIds: undefined,
    startSecondsOffset,
    endSecondsOffset,
    karaokeConfigId: migratedConfig.id,
  };
}

/**
 * Serializes karaoke clips to legacy attributes on tokens.
 *
 * This function:
 * 1. Takes an array of transcript tokens
 * 2. Takes an array of karaoke lines with clip boundaries
 * 3. Marks the last token of each line with karaoke_break = true
 * 4. Adds time offsets to first/last tokens from line metadata
 * 5. Adds karaoke attributes to hidden/muted tokens within each line
 *
 * @param tokens - Array of transcript tokens to modify
 * @param karaokeLines - Array of karaoke lines with clip boundaries
 * @returns Modified array of transcript tokens with karaoke attributes
 */
export function serializeKaraokeClipsToLegacyAttributesOnTokens(
  tokens: TranscriptElement[],
  karaokeLines: KaraokeLine[],
): TranscriptElement[] {
  // Create a map of token ID to token for quick lookup
  const tokenMap = new Map<string, TranscriptToken>();
  tokens.forEach(token => {
    if (token.id) {
      tokenMap.set(token.id, token as TranscriptToken);
    }
  });

  // Process each karaoke line
  karaokeLines.forEach(line => {
    const lineTokens = line.transcriptTokens;
    if (lineTokens.length === 0) return;

    const hiddenTokenIds = new Set(line.allHiddenTranscriptTokenIds || []);
    let lastTextTokenInLine: TranscriptToken | null = null;
    // Process all tokens in the line (including hidden ones)
    lineTokens.forEach((lineToken, index) => {
      const tokenInMap = tokenMap.get(lineToken.id);
      if (!tokenInMap) return;

      if (tokenInMap.type === 'text') {
        lastTextTokenInLine = tokenInMap;
      }

      // Set mute attribute if token is hidden
      if (hiddenTokenIds.has(lineToken.id)) {
        tokenInMap.state = 'muted';
      }

      // Mark last token with karaoke_break
      if (index === lineTokens.length - 1) {
        tokenInMap.karaoke_break = true;
      }

      // Add start time offset to first token
      if (index === 0 && line.startSecondsOffset !== undefined) {
        tokenInMap.karaoke_break_start_ts_diff = line.startSecondsOffset;
      }

      // Add end time offset to last token
      if (
        index === lineTokens.length - 1 &&
        line.endSecondsOffset !== undefined &&
        lastTextTokenInLine
      ) {
        lastTextTokenInLine.karaoke_break_end_ts_diff = line.endSecondsOffset;
      }
    });
  });

  return tokens;
}

export function shiftLegacyTranscriptAttributesFromWhitespaces(
  transcriptElements: TranscriptElementLegacy[],
): TranscriptElementLegacy[] {
  // moves karaoke break, break start/end offsets from whitespace element to closest non-removed non-whitespace element before it (for break and end offset) or after it (for start offset)
  const shiftedElements = transcriptElements.map(el => ({ ...el })); // shallow copy

  for (let i = 0; i < shiftedElements.length; i++) {
    const element = shiftedElements[i];
    if (
      element.state === 'removed' ||
      element.state === 'cut' ||
      element.value?.trim() !== ''
    ) {
      continue;
    }

    if (element.value?.trim() === '') {
      // Shift karaoke_break to previous non-whitespace element
      if (element.karaoke_break) {
        for (let j = i - 1; j >= 0; j--) {
          if (
            shiftedElements[j].state !== 'removed' &&
            shiftedElements[j].state !== 'cut'
          ) {
            if (shiftedElements[j].value?.trim() !== '') {
              shiftedElements[j].karaoke_break = true;
              break;
            }
            if (shiftedElements[j].karaoke_break) {
              break;
            }
          }
        }
        delete element.karaoke_break;
      }

      // Shift karaoke_break_end_ts_diff to previous non-whitespace element
      if (element.karaoke_break_end_ts_diff !== undefined) {
        for (let j = i - 1; j >= 0; j--) {
          if (
            shiftedElements[j].state !== 'removed' &&
            shiftedElements[j].state !== 'cut'
          ) {
            if (shiftedElements[j].value?.trim() !== '') {
              shiftedElements[j].karaoke_break_end_ts_diff =
                element.karaoke_break_end_ts_diff;
              break;
            }
            if (shiftedElements[j].karaoke_break) {
              break;
            }
          }
        }
        delete element.karaoke_break_end_ts_diff;
      }

      // Shift karaoke_break_start_ts_diff to next non-whitespace element
      if (element.karaoke_break_start_ts_diff !== undefined) {
        for (let j = i + 1; j < shiftedElements.length; j++) {
          if (
            shiftedElements[j].state !== 'removed' &&
            shiftedElements[j].state !== 'cut'
          ) {
            if (shiftedElements[j].value?.trim() !== '') {
              shiftedElements[j].karaoke_break_start_ts_diff =
                element.karaoke_break_start_ts_diff;
              break;
            }
            if (shiftedElements[j].karaoke_break) {
              break;
            }
          }
        }
        delete element.karaoke_break_start_ts_diff;
      }

      // Shift karaoke_break to previous non-whitespace element
      if (element.last_photo_highlight) {
        for (let j = i - 1; j >= 0; j--) {
          if (
            shiftedElements[j].state !== 'removed' &&
            shiftedElements[j].state !== 'cut'
          ) {
            if (shiftedElements[j].value?.trim() !== '') {
              if (shiftedElements[j].photo_highlight_id) {
                shiftedElements[j].last_photo_highlight = true;
              }
              break;
            }
          }
        }
        delete element.karaoke_break;
      }
    }
  }
  return shiftedElements;
}
